import {
  ImportInvoiceOperationResult,
  extractAxiosErrorMessage,
  useAsyncOperationWithResult,
  useSiteOperationalActions,
} from "@inrange/building-manager-api-client";
import { Site } from "@inrange/building-manager-api-client/models-site";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InvoicePreviewModal from "../billing/InvoicePreviewModal";
import {
  computeMonthsToProcess,
  formatDateYMD,
  operationStatusMessage,
} from "./utils";

const TransferReadingsInvoice = ({ site }: { site: Site }) => {
  const { uploadAndProcessTransferReadings } = useSiteOperationalActions();

  const monthsToProcess = useMemo(
    () =>
      computeMonthsToProcess(
        site.commerciallyOperationalDate ?? undefined,
        site.timeZone
      ),
    [site.commerciallyOperationalDate, site.timeZone]
  );

  const [selectedMonth, setSelectedMonth] = useState<string | undefined>();
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date());
  const [isProcessing, setIsProcessing] = useState(false);
  const [invoicePreviewMonth, setInvoicePreviewMonth] = useState<string | null>(
    null
  );
  const [uploadProgress, setUploadProgress] = useState<{
    current: number;
    total: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (monthsToProcess.length === 0) {
      setSelectedMonth(undefined);
      return;
    }
    if (!selectedMonth || !monthsToProcess.includes(selectedMonth)) {
      setSelectedMonth(monthsToProcess[monthsToProcess.length - 1]);
    }
  }, [monthsToProcess, selectedMonth]);

  const onSettled = useCallback(() => {
    setIsProcessing(false);
  }, []);

  const { asyncOperation, setOperationKey, result, setResult } =
    useAsyncOperationWithResult<ImportInvoiceOperationResult>({
      siteId: site.id,
      onSettled,
    });

  const hasTransferReadingsForMonth = useMemo(() => {
    if (!selectedMonth) return false;
    return (site.siteFiles ?? []).some(
      (f) =>
        f.fileType === "importReading" && f.fileName.startsWith(selectedMonth)
    );
  }, [site.siteFiles, selectedMonth]);

  const handleMultiFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setUploadFiles(files);
  };

  const runUploadAndProcess = async (generateInvoice: boolean) => {
    if (uploadFiles.length === 0 || !selectedMonth) return;
    setResult(null);
    setIsProcessing(true);
    setUploadProgress(null);
    try {
      const response = await uploadAndProcessTransferReadings.mutateAsync({
        siteId: site.id,
        month: selectedMonth,
        files: uploadFiles,
        generateInvoice,
        invoiceDate: formatDateYMD(invoiceDate),
        onUploadProgress: (current, total) =>
          setUploadProgress({ current, total }),
      });
      setUploadProgress(null);
      setOperationKey(response.operationKey);
      setUploadFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      setUploadProgress(null);
      const errorMessage = await extractAxiosErrorMessage(error);
      setResult({ type: "error", message: errorMessage });
      setIsProcessing(false);
    }
  };

  const statusMessage = (generateInvoice: boolean) =>
    operationStatusMessage(asyncOperation.data?.status, {
      progress: asyncOperation.data?.progress,
      uploadProgress: uploadProgress ?? undefined,
      generateInvoice,
    });

  return (
    <>
      <div style={{ fontWeight: "600" }}>
        Upload import readings and invoice
      </div>
      <div style={{ fontSize: "0.9em", color: "#666" }}>
        Upload Axpo XLSX files for import readings — one per import match MPAN.
        The backend validates month and MPAN matching.
      </div>
      <Row className="g-2 align-items-end">
        <Col style={{ width: "220px", flex: "0 0 auto" }}>
          <Form.Group controlId="importMonthToProcess">
            <Form.Label>Month</Form.Label>
            <Form.Select
              value={selectedMonth ?? ""}
              onChange={(e) => setSelectedMonth(e.target.value || undefined)}
              disabled={monthsToProcess.length === 0 || isProcessing}
            >
              {monthsToProcess.length === 0 ? (
                <option value="">No complete months available</option>
              ) : (
                monthsToProcess
                  .slice()
                  .reverse()
                  .map((month, index) => (
                    <option key={month} value={month}>
                      {month}
                      {index === 0 && " (latest)"}
                    </option>
                  ))
              )}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col style={{ width: "300px", flex: "0 0 auto" }}>
          <Form.Group controlId="uploadFiles">
            <Form.Label>Axpo XLSX files</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              multiple
              onChange={handleMultiFileChange}
              disabled={isProcessing}
              ref={fileInputRef}
            />
          </Form.Group>
        </Col>
        <Col style={{ width: "220px", flex: "0 0 auto" }}>
          <Form.Group controlId="importInvoiceDate">
            <Form.Label>Invoice date</Form.Label>
            <div>
              <DatePicker
                selected={invoiceDate}
                onChange={(date: Date | null) => {
                  if (date) setInvoiceDate(date);
                }}
                dateFormat="dd MMM yyyy"
                calendarStartDay={1}
                disabled={isProcessing}
                className="form-control"
              />
            </div>
          </Form.Group>
        </Col>
      </Row>
      <Row className="g-2 align-items-end">
        <Col sm="auto" className="d-flex align-items-center gap-2">
          <Button
            variant="primary"
            onClick={() => runUploadAndProcess(true)}
            disabled={
              uploadFiles.length === 0 || !selectedMonth || isProcessing
            }
          >
            {isProcessing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {statusMessage(true)}
              </>
            ) : (
              "Upload and generate invoice"
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => runUploadAndProcess(false)}
            disabled={
              uploadFiles.length === 0 || !selectedMonth || isProcessing
            }
          >
            Upload only
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => {
              if (!selectedMonth) return;
              setInvoicePreviewMonth(selectedMonth);
            }}
            disabled={
              !selectedMonth || isProcessing || !hasTransferReadingsForMonth
            }
          >
            Generate invoice w/ adjustments
            {hasTransferReadingsForMonth ? "" : " (upload readings first)"}
          </Button>
        </Col>
      </Row>

      {result && (
        <Alert
          variant={result.type === "success" ? "success" : "danger"}
          dismissible
          onClose={() => setResult(null)}
        >
          {result.type === "success" && result.response ? (
            <>
              <div>Operation completed successfully.</div>
              {result.response.file_results.length > 0 && (
                <div className="mt-1">
                  {result.response.file_results.map((fr, i) => {
                    const fileName = fr.fileName as string | undefined;
                    const readingsCount = fr.readingsCount as number;
                    const exporterFilesCreated =
                      (fr.exporterFilesCreated as number) ?? 0;
                    const importerFilesCreated =
                      (fr.importerFilesCreated as number) ?? 0;
                    const exportGridConnectionId = fr.exportGridConnectionId as
                      | string
                      | undefined;
                    const actions: string[] = [];
                    if (fr.readingsProcessed) {
                      actions.push(`${readingsCount} readings loaded`);
                    } else {
                      actions.push(
                        `${readingsCount} readings skipped (newer data exists)`
                      );
                    }
                    if (exporterFilesCreated > 0 && importerFilesCreated > 0) {
                      actions.push(
                        "files attached to exporting site and importing site"
                      );
                    } else if (exporterFilesCreated > 0) {
                      actions.push("file attached to exporting site");
                    } else if (importerFilesCreated > 0) {
                      actions.push("file attached to importing site");
                    }
                    return (
                      <div key={i} className={i > 0 ? "mt-1" : ""}>
                        <div>
                          <strong>
                            File {i + 1}: {fileName ?? `file ${i + 1}`}
                          </strong>
                        </div>
                        {exportGridConnectionId && (
                          <div>
                            Matched to export MPAN: {exportGridConnectionId}
                          </div>
                        )}
                        {actions.length > 0 && (
                          <div>Actions: {actions.join(", ")}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {result.response.files_attached.length > 0 && (
                <div className="mt-2">
                  <div className="fw-bold">Invoice files attached:</div>
                  <ul>
                    {result.response.files_attached.map((f, index) => (
                      <li key={index}>{f.fileName}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            result.message
          )}
        </Alert>
      )}

      {invoicePreviewMonth && (
        <InvoicePreviewModal
          siteId={site.id}
          month={invoicePreviewMonth}
          currencyCode={site.currencyCode}
          initialInvoiceDate={invoiceDate}
          onClose={() => setInvoicePreviewMonth(null)}
          isImportInvoice
        />
      )}
    </>
  );
};

export default TransferReadingsInvoice;
